/**
 * PageFileViewerDialog
 *
 * Full-screen-ish modal wrapping `UniversalFileViewer` for in-app file
 * preview. Header shows the file's icon + name, footer surfaces the
 * creation date plus "Details" and "Download" CTAs.
 *
 * Why this wrapper exists: the page kept the modal chrome (sizing,
 * footer actions, error toasting) inline alongside the universal viewer
 * itself. Splitting the chrome out keeps the page slim and lets the
 * viewer be reused elsewhere without dragging this dialog along.
 */

import { type FileItem } from "@crm-src/lib/files-api"
import {
  Box,
  Button,
  Dialog,
  Typography,
} from "@mui/material"
import { Calendar, Eye, Download } from "lucide-react"
import { UniversalFileViewer } from "./universal-file-viewer"
import { FileTypeIcon } from "./file-type-icon"
import { formatFileDate } from "./format"

interface PageFileViewerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedFile: FileItem | null
  onError: (message: string) => void
  onDownload: (file: FileItem) => void
  /** Switch from the viewer dialog to the read-only details dialog. */
  onShowDetails: (file: FileItem) => void
}

export function PageFileViewerDialog({
  open,
  onOpenChange,
  selectedFile,
  onError,
  onDownload,
  onShowDetails,
}: PageFileViewerDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={() => onOpenChange(false)}
      maxWidth={false}
      PaperProps={{
        sx: {
          overflow: "hidden",
          width: "95vw",
          maxWidth: "95vw",
          height: "90vh",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {selectedFile && (
        <>
          <Box sx={{ p: 1.5, pb: 0.5 }}>
            <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FileTypeIcon file={selectedFile} />
              {selectedFile.name}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {selectedFile.content_type} • {selectedFile.file_size_formatted}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
            <UniversalFileViewer
              file={selectedFile}
              onError={onError}
              onDownload={() => onDownload(selectedFile)}
            />
          </Box>

          <Box sx={{ p: 3, pt: 1, borderTop: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
                <Calendar size={16} />
                <Typography variant="body2">Created: {formatFileDate(selectedFile.created_at)}</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<Eye size={16} />}
                  onClick={() => onShowDetails(selectedFile)}
                >
                  Details
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Download size={16} />}
                  onClick={() => {
                    onDownload(selectedFile)
                    onOpenChange(false)
                  }}
                >
                  Download
                </Button>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Dialog>
  )
}
